import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import technicianLocationService from '../services/technicianLocationService';
import { getSocket, joinShopRoom, leaveShopRoom } from '../utils/socket';
import { toast } from 'react-toastify';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  FiMapPin,
  FiNavigation,
  FiBatteryCharging,
  FiCompass,
  FiPhone,
  FiRefreshCw,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiActivity,
  FiCrosshair
} from 'react-icons/fi';
import './TechnicianLiveMap.css';

// Fix for default Leaflet icon paths in Vite/Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TechnicianLiveMap = () => {
  const { user } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTech, setSelectedTech] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [routeHistory, setRouteHistory] = useState(null);
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({}); // { [techId]: L.Marker }
  const polylineRef = useRef(null); // L.Polyline for route history

  const shopId = user?.shop?._id || user?._id;

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    // Default center (can be tuned or auto-focused to technicians)
    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
    }).setView([19.0760, 72.8777], 11); // Default Mumbai / shop area

    L.control.zoom({ position: 'topright' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Fetch initial live technicians
  const fetchLiveLocations = async () => {
    try {
      setLoading(true);
      const res = await technicianLocationService.getLiveLocations();
      if (res.success) {
        setTechnicians(res.data);
        renderTechnicianMarkers(res.data);
      }
    } catch (err) {
      toast.error('Failed to load technician locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveLocations();
  }, []);

  // Socket.IO real-time updates listener
  useEffect(() => {
    if (!shopId) return;

    joinShopRoom(shopId);
    const socket = getSocket();

    const handleLocationUpdate = (payload) => {
      setTechnicians((prev) => {
        const index = prev.findIndex((t) => t._id === payload.technicianId);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: payload.status || updated[index].status,
            currentLocation: payload.currentLocation,
            isTrackingActive: payload.isTrackingActive,
          };
          updateSingleMarker(updated[index]);
          return updated;
        } else {
          return prev;
        }
      });
    };

    socket.on('technician:location_updated', handleLocationUpdate);

    return () => {
      socket.off('technician:location_updated', handleLocationUpdate);
      leaveShopRoom(shopId);
    };
  }, [shopId]);

  // Create custom pulsing HTML marker for technician
  const createTechIcon = (tech) => {
    const statusClass = `marker-${tech.status || 'available'}`;
    const initials = tech.name
      ? tech.name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : 'TC';

    return L.divIcon({
      className: 'leaflet-tech-marker',
      html: `
        <div class="leaflet-tech-marker ${statusClass}">
          <div class="marker-pulse"></div>
          <div class="marker-inner">${initials}</div>
        </div>
      `,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -22],
    });
  };

  // Render or update all markers on the map
  const renderTechnicianMarkers = (techList) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const bounds = [];

    techList.forEach((tech) => {
      const coords = tech.currentLocation?.coordinates;
      if (coords && coords.length === 2) {
        const [lng, lat] = coords;
        bounds.push([lat, lng]);

        if (markersRef.current[tech._id]) {
          // Update existing marker position
          markersRef.current[tech._id].setLatLng([lat, lng]);
          markersRef.current[tech._id].setIcon(createTechIcon(tech));
        } else {
          // Create new marker
          const marker = L.marker([lat, lng], {
            icon: createTechIcon(tech),
          }).addTo(map);

          marker.bindPopup(buildPopupHtml(tech));
          marker.on('click', () => setSelectedTech(tech));
          markersRef.current[tech._id] = marker;
        }
      }
    });

    // Auto-fit bounds if we have valid locations
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
    }
  };

  // Update a single marker when real-time socket event arrives
  const updateSingleMarker = (tech) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const coords = tech.currentLocation?.coordinates;
    if (coords && coords.length === 2) {
      const [lng, lat] = coords;

      if (markersRef.current[tech._id]) {
        markersRef.current[tech._id].setLatLng([lat, lng]);
        markersRef.current[tech._id].setIcon(createTechIcon(tech));
        markersRef.current[tech._id].setPopupContent(buildPopupHtml(tech));
      } else {
        const marker = L.marker([lat, lng], {
          icon: createTechIcon(tech),
        }).addTo(map);
        marker.bindPopup(buildPopupHtml(tech));
        marker.on('click', () => setSelectedTech(tech));
        markersRef.current[tech._id] = marker;
      }
    }
  };

  // Build Popup Content HTML
  const buildPopupHtml = (tech) => {
    const speed = tech.currentLocation?.speed
      ? Math.round(tech.currentLocation.speed * 3.6)
      : 0;
    const battery = tech.currentLocation?.batteryLevel ?? '--';
    const lastUpdate = tech.currentLocation?.lastUpdated
      ? new Date(tech.currentLocation.lastUpdated).toLocaleTimeString()
      : 'Never';

    return `
      <div style="font-family: inherit; min-width: 180px; padding: 4px;">
        <div style="font-weight: 700; font-size: 14px; color: #111827; margin-bottom: 2px;">
          ${tech.name}
        </div>
        <div style="font-size: 11px; color: #6b7280; margin-bottom: 8px;">
          ID: ${tech.employeeId || 'N/A'} &bull; ${tech.phone}
        </div>
        <div style="display: flex; gap: 8px; margin-bottom: 8px; font-size: 11px;">
          <span style="background: #e5e7eb; padding: 2px 6px; border-radius: 4px; font-weight: 600;">
            ${(tech.status || 'available').replace('_', ' ').toUpperCase()}
          </span>
          <span style="color: #059669; font-weight: 600;">🔋 ${battery}%</span>
          <span style="color: #2563eb; font-weight: 600;">🚗 ${speed} km/h</span>
        </div>
        <div style="font-size: 10px; color: #9ca3af; border-top: 1px solid #f3f4f6; pt: 4px;">
          Updated: ${lastUpdate}
        </div>
      </div>
    `;
  };

  // Center on clicked technician
  const focusTechnician = (tech) => {
    setSelectedTech(tech);
    const map = mapInstanceRef.current;
    const coords = tech.currentLocation?.coordinates;

    if (coords && coords.length === 2 && map) {
      const [lng, lat] = coords;
      map.flyTo([lat, lng], 15, { duration: 1.2 });
      if (markersRef.current[tech._id]) {
        markersRef.current[tech._id].openPopup();
      }
    } else {
      toast.info(`${tech.name} has not transmitted GPS coordinates yet`);
    }
  };

  // Fetch and display historical route polyline
  const loadRouteHistory = async () => {
    if (!selectedTech) {
      toast.warning('Please select a technician first');
      return;
    }

    try {
      setHistoryLoading(true);
      const res = await technicianLocationService.getRouteHistory(selectedTech._id, historyDate);
      if (res.success) {
        setRouteHistory(res.data);
        drawRoutePolyline(res.data);
      }
    } catch (err) {
      toast.error('Failed to load route history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const drawRoutePolyline = (points) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    if (!points || points.length === 0) {
      toast.info('No route data found for this date');
      return;
    }

    const latlngs = points
      .filter((p) => p.location?.coordinates && p.location.coordinates.length === 2)
      .map((p) => [p.location.coordinates[1], p.location.coordinates[0]]);

    if (latlngs.length > 0) {
      polylineRef.current = L.polyline(latlngs, {
        color: '#0087f0',
        weight: 5,
        opacity: 0.8,
        dashArray: '8, 8',
      }).addTo(map);

      map.fitBounds(polylineRef.current.getBounds(), { padding: [50, 50] });
      toast.success(`Rendered ${latlngs.length} GPS breadcrumb points`);
    }
  };

  const clearRouteHistory = () => {
    const map = mapInstanceRef.current;
    if (map && polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    setRouteHistory(null);
  };

  // Quick simulation helper for local test ping
  const handleSimulatePing = async (tech) => {
    if (!tech) return;
    const baseLng = tech.currentLocation?.coordinates?.[0] || 72.8777;
    const baseLat = tech.currentLocation?.coordinates?.[1] || 19.0760;

    // Jitter randomly by ~50-100m
    const newLng = baseLng + (Math.random() - 0.5) * 0.005;
    const newLat = baseLat + (Math.random() - 0.5) * 0.005;

    try {
      await technicianLocationService.updateLocation({
        technicianId: tech._id,
        latitude: newLat,
        longitude: newLng,
        speed: 12.5,
        batteryLevel: 88,
        status: tech.status || 'en_route',
      });
      toast.success(`Location ping sent for ${tech.name}`);
    } catch (err) {
      toast.error('Simulation failed');
    }
  };

  // Filter technicians
  const filteredTechnicians = technicians.filter((tech) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'tracking') return tech.isTrackingActive;
    return tech.status === filterStatus;
  });

  const totalCount = technicians.length;
  const onDutyCount = technicians.filter((t) => t.status === 'en_route' || t.status === 'on_site').length;
  const availableCount = technicians.filter((t) => t.status === 'available').length;

  return (
    <div className="live-map-container">
      {/* Sidebar Overlay */}
      <div className="live-map-sidebar">
        <div className="sidebar-top">
          <div className="map-header-title">
            <h2>
              <FiNavigation style={{ color: 'var(--primary-600)' }} /> Fleet Tracker
            </h2>
            <span className="live-indicator-badge">
              <span className="live-dot"></span> Live
            </span>
          </div>

          <div className="map-stats-grid">
            <div className="stat-pill">
              <span className="number">{totalCount}</span>
              <span className="label">Total</span>
            </div>
            <div className="stat-pill">
              <span className="number" style={{ color: 'var(--primary-600)' }}>
                {onDutyCount}
              </span>
              <span className="label">On Duty</span>
            </div>
            <div className="stat-pill">
              <span className="number" style={{ color: 'var(--success-600)' }}>
                {availableCount}
              </span>
              <span className="label">Available</span>
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="map-filter-bar">
          <button
            className={`filter-chip ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({totalCount})
          </button>
          <button
            className={`filter-chip ${filterStatus === 'en_route' ? 'active' : ''}`}
            onClick={() => setFilterStatus('en_route')}
          >
            En Route
          </button>
          <button
            className={`filter-chip ${filterStatus === 'on_site' ? 'active' : ''}`}
            onClick={() => setFilterStatus('on_site')}
          >
            On Site
          </button>
          <button
            className={`filter-chip ${filterStatus === 'available' ? 'active' : ''}`}
            onClick={() => setFilterStatus('available')}
          >
            Available
          </button>
        </div>

        {/* Technician Cards Scroll */}
        <div className="technician-list-scroll">
          {filteredTechnicians.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
              No technicians found with current filter
            </div>
          ) : (
            filteredTechnicians.map((tech) => {
              const isSelected = selectedTech?._id === tech._id;
              const hasCoords = tech.currentLocation?.coordinates?.length === 2;
              const statusClass = `status-${tech.status || 'available'}`;

              return (
                <div
                  key={tech._id}
                  className={`tech-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => focusTechnician(tech)}
                >
                  <div className="tech-card-header">
                    <div className="tech-name-role">
                      <div className="tech-avatar">
                        {tech.name ? tech.name[0].toUpperCase() : 'T'}
                      </div>
                      <div>
                        <div className="tech-name">{tech.name}</div>
                        <div className="tech-id">{tech.employeeId || 'ID: --'} &bull; {tech.phone}</div>
                      </div>
                    </div>
                    <span className={`status-badge-sm ${statusClass}`}>
                      {(tech.status || 'available').replace('_', ' ')}
                    </span>
                  </div>

                  <div className="tech-telemetry-row">
                    <div className="telemetry-item">
                      <FiMapPin style={{ color: hasCoords ? 'var(--primary-600)' : 'var(--gray-400)' }} />
                      <span>{hasCoords ? 'GPS Active' : 'No Signal'}</span>
                    </div>
                    <div className="telemetry-item">
                      <FiBatteryCharging style={{ color: 'var(--success-500)' }} />
                      <span>{tech.currentLocation?.batteryLevel ?? '--'}%</span>
                    </div>
                    {tech.currentLocation?.lastUpdated && (
                      <div className="telemetry-item" style={{ marginLeft: 'auto' }}>
                        <FiClock />
                        <span>{new Date(tech.currentLocation.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                  </div>

                  {/* Active Site Visit Info if assigned */}
                  {tech.activeSiteVisit && (
                    <div className="tech-active-job">
                      <div className="job-customer">
                        Job: {tech.activeSiteVisit.customer?.name || 'Customer'}
                      </div>
                      <div className="job-address">
                        {tech.activeSiteVisit.customer?.address?.street || 'Site Address'}, {tech.activeSiteVisit.customer?.address?.city || ''}
                      </div>
                    </div>
                  )}

                  {/* Quick simulation button */}
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
                    <button
                      className="filter-chip"
                      style={{ fontSize: '0.6875rem', padding: '0.2rem 0.5rem' }}
                      title="Simulate GPS Movement for testing"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSimulatePing(tech);
                      }}
                    >
                      <FiActivity /> Sim Ping
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Map Canvas */}
      <div className="map-canvas-area">
        <div id="leaflet-map" ref={mapContainerRef}></div>

        {/* Top Right Map Overlay Toolbar */}
        <div className="map-overlay-toolbar">
          <button className="map-btn" onClick={fetchLiveLocations}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>

        {/* Route History Drawer (When a tech is selected) */}
        {selectedTech && (
          <div className="route-history-panel">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--gray-900)' }}>
                {selectedTech.name} &bull; Route Playback
              </div>
              <input
                type="date"
                value={historyDate}
                onChange={(e) => setHistoryDate(e.target.value)}
                style={{
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                }}
              />
              <button
                className="map-btn"
                style={{ padding: '0.35rem 0.75rem' }}
                onClick={loadRouteHistory}
                disabled={historyLoading}
              >
                <FiCalendar /> {historyLoading ? 'Loading...' : 'View Trail'}
              </button>
              {routeHistory && (
                <button
                  className="map-btn"
                  style={{ padding: '0.35rem 0.5rem', color: 'var(--error-500)' }}
                  onClick={clearRouteHistory}
                >
                  Clear Trail
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="map-btn"
                style={{ background: 'var(--primary-600)', color: 'white' }}
                onClick={() => focusTechnician(selectedTech)}
              >
                <FiCrosshair /> Center Tech
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicianLiveMap;
